// Vanilla port of FlightCard.tsx (grid + list variants).
import { formatFlightDateShort, formatGroundTime, formatLegDuration, isNextDay } from './flightFormat.js';
// import { createAlertButton } from './create-alert.js';

const ICON_PLANE = (s = 14) => `<svg width="${s}" height="${s}" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5L21 16z"/></svg>`;
const ICON_CHEVRON = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden><path d="M6 9l6 6 6-6"/></svg>`;
const ICON_ARROW = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden><path d="M5 12h14M12 5l7 7-7 7"/></svg>`;
const ICON_CLOCK = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>`;

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function groundBand(hours, city, { detailed = false, tail, overnight = false } = {}) {
  const tailHtml = tail
    ? `<span class="ground-tail">${esc(tail.from)} → ${esc(tail.to)}${tail.overnight ? ' +1' : ''}<br/>landing to take-off</span>`
    : '';
  return `<div class="ground"><div class="ground-icon">${ICON_CLOCK}</div><div>
    <div class="ground-amount">${esc(formatGroundTime(hours, city, detailed))}</div>
    <div class="ground-label">Time at destination${overnight ? ' · 1 night' : ''}</div>
  </div>${tailHtml}</div>`;
}

function listLeg({ direction, depTime, depAirport, arrTime, arrAirport, depDate, arrDate, compareDate, isReturn = false }) {
  const duration = formatLegDuration(depTime, arrTime, depDate, arrDate);
  const arrNextDay = depDate && arrDate ? isNextDay(depDate, arrDate) : false;
  const depNextDay = compareDate && depDate ? isNextDay(compareDate, depDate) : false;
  return `<div class="leg${isReturn ? ' return-leg' : ''}">
    <div class="leg-dir"><span class="dir-icon">${ICON_PLANE(13)}</span><span>${direction}</span></div>
    <div class="endpoint depart"><span class="time">${esc(depTime)}${depNextDay ? '<span class="day-tag">+1</span>' : ''}</span><span class="airport">${esc(depAirport)}</span></div>
    <div class="path"><span class="duration">${esc(duration)}</span><div class="line"><span class="plane">${ICON_PLANE()}</span></div><span class="stops">Direct</span></div>
    <div class="endpoint arrive"><span class="time">${esc(arrTime)}${arrNextDay ? '<span class="day-tag">+1</span>' : ''}</span><span class="airport">${esc(arrAirport)}</span></div>
  </div>`;
}

function gridCard(flight, id) {
  const price = Math.round(flight.price);
  const returnDisplayTime = flight.return_arrival_time ?? flight.return_time ?? '—';
  const returnDisplayCode = flight.return_arrival_time ? flight.departure_airport : flight.destination_airport;
  const el = document.createElement('article');
  el.className = 'fcard';
  if (id) el.id = id;
  el.innerHTML = `
    <div class="fcard-head">
      <div><div class="city">${esc(flight.city_to)}</div><div class="date">${esc(formatFlightDateShort(flight.departure_date))}</div></div>
      <div class="price-wrap"><div class="price-label">From</div><div class="price">£${price}</div><div class="price-unit">per person</div></div>
    </div>
    <div class="times">
      <div class="stamp"><span class="stamp-time">${esc(flight.departure_time)}</span><span class="stamp-code">${esc(flight.departure_airport)}</span></div>
      <button type="button" class="details-link" aria-expanded="false" data-details>Flight details ${ICON_CHEVRON}</button>
      <div class="stamp end"><span class="stamp-time">${esc(returnDisplayTime)}</span><span class="stamp-code">${esc(returnDisplayCode)}</span></div>
    </div>
    <div class="pathline"><span class="plane">${ICON_PLANE(13)}</span></div>
    <div class="detail-panel" data-detail-panel>
      <div class="detail-row"><span class="lbl">Route</span><span class="val">${esc(flight.departure_airport)} → ${esc(flight.city_to)} → ${esc(flight.departure_airport)}</span></div>
      <div class="detail-row"><span class="lbl">Dates</span><span class="val">${esc(flight.to_and_from)}</span></div>
      <div class="detail-row"><span class="lbl">Outbound</span><span class="val">${esc(flight.departure_time)} → ${esc(flight.departure_arrival_time ?? '—')} · Direct</span></div>
      ${flight.return_time ? `<div class="detail-row"><span class="lbl">Return</span><span class="val">${esc(flight.return_time)} → ${esc(flight.return_arrival_time ?? '—')} · Direct</span></div>` : ''}
    </div>
    ${flight.hours_at_destination != null ? groundBand(flight.hours_at_destination, flight.city_to) : ''}
    ${flight.flights_within_50_diff != null ? `<div class="alt-line"><b>${flight.flights_within_50_diff}</b> other flight${flight.flights_within_50_diff === 1 ? '' : 's'} found within £50</div>` : ''}
    <a href="${esc(flight.link)}" target="_blank" rel="noopener noreferrer" class="book">Book now for £${price} pp ${ICON_ARROW}</a>`;
  const detailsBtn = el.querySelector('[data-details]');
  const panel = el.querySelector('[data-detail-panel]');
  detailsBtn.addEventListener('click', () => {
    const open = panel.classList.toggle('open');
    detailsBtn.setAttribute('aria-expanded', String(open));
  });
  // el.appendChild(createAlertButton({ maxPrice: price, flyTo: flight.destination_airport, className: 'alert-cta-btn mt-2 w-full' }));
  return el;
}

function listCard(flight, id) {
  const price = Math.round(flight.price);
  const overnight = flight.return_date != null && isNextDay(flight.departure_date, flight.return_date);
  const groundTail = flight.departure_arrival_time && flight.return_time
    ? { from: flight.departure_arrival_time, to: flight.return_time, overnight }
    : undefined;
  const el = document.createElement('article');
  el.className = 'flight-card';
  if (id) el.id = id;
  el.innerHTML = `
    <div class="itinerary">
      <div class="trip-meta"><div class="trip-city">${esc(flight.city_to)}</div><div class="trip-dates">${esc(flight.to_and_from)}</div></div>
      ${listLeg({ direction: 'Out', depTime: flight.departure_time, depAirport: flight.departure_airport, arrTime: flight.departure_arrival_time ?? '—', arrAirport: flight.destination_airport, depDate: flight.departure_date, arrDate: flight.departure_arrival_date })}
      ${flight.hours_at_destination != null ? groundBand(flight.hours_at_destination, flight.city_to, { detailed: true, tail: groundTail, overnight }) : ''}
      ${flight.return_time ? listLeg({ direction: 'Back', depTime: flight.return_time, depAirport: flight.destination_airport, arrTime: flight.return_arrival_time ?? '—', arrAirport: flight.departure_airport, depDate: flight.return_date ?? undefined, arrDate: flight.return_arrival_date ?? flight.return_date ?? undefined, compareDate: flight.departure_date, isReturn: true }) : ''}
    </div>
    <div class="fare">
      <div class="price-block"><span class="price-label">Return total</span><span class="price">£${price}</span><span class="price-note">per person</span></div>
      <a href="${esc(flight.link)}" target="_blank" rel="noopener noreferrer" class="book-btn">Book pair ${ICON_ARROW}</a>
    </div>`;
  // el.querySelector('.fare').appendChild(createAlertButton({ maxPrice: price, flyTo: flight.destination_airport, className: 'alert-cta-btn mt-2 w-full text-sm' }));
  return el;
}

export function renderFlightCard(flight, { id, variant = 'list' } = {}) {
  return variant === 'grid' ? gridCard(flight, id) : listCard(flight, id);
}
